/**
 * The employee shell — phase 2 §7.1 and §7.2.
 *
 * One of three surfaces, not the only one: §7.1 makes Employee, Schema, and the
 * client portal "separate route trees with separate shells, not one tree
 * rendering conditionally". Schema is `layout/schema-shell.tsx`; the portal is
 * Phase 4.
 *
 * **The navigation is §7.2's, and what it drops is the point.** The three
 * person-scoped queues — reviews due, review waiting on you, acknowledgments —
 * are sections of Home rather than three sidebar entries, because "three queues
 * in a sidebar is the console pattern again". Introspection moved behind Schema
 * and provisioning to Settings, since filing a new hire under a database icon
 * misnames a recurring business task (§7.3).
 *
 * The nav is dimmed against §4.4's role baselines rather than hidden, so a
 * `staff` member reading "Your role does not carry user.manage" learns the
 * shape of the model instead of wondering where a page went. Dimming is
 * presentation only — every route behind it is authorized again by the API,
 * which resolves the role from the database rather than from anything this file
 * believes (§4.7).
 */

import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Bell01, Home05 } from '@untitledui/icons';
import type { FC, ReactNode, SVGProps } from 'react';
import { useMe, useSignOut, useUnreadCount } from '../api/queries.ts';
import type { UserRole } from '../api/types.ts';
import { type Capability, roleCarries } from '../components/capabilities.ts';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import { cn } from '../components/ui/cn.ts';
import { Loading } from '../components/ui/feedback.tsx';
import { AcknowledgmentBanner } from './acknowledgment-banner.tsx';
import { ShellBar } from './shell-bar.tsx';
import {
  SIDEBAR_ID,
  SidebarResizeHandle,
  SidebarToggle,
  sidebarClasses,
  sidebarStyle,
  useSidebar,
} from './sidebar.tsx';

interface NavItem {
  to: string;
  label: string;
  /** The role capability this section is *about*; absent means everyone. */
  capability?: Capability;
  description: string;
  icon?: FC<SVGProps<SVGSVGElement> & { size?: number }>;
}

/** §7.2's tree, in its order. The divider is where the day-to-day work ends. */
const NAV: NavItem[] = [
  {
    to: '/',
    label: 'Home',
    icon: Home05,
    description: 'What is waiting on you, and the projects you can reach',
  },
  /**
   * Not a §7.2 section — Home carries the count — but the way into the full
   * list, and it sits under Home for that reason rather than below the divider.
   */
  {
    to: '/notifications',
    label: 'Notifications',
    icon: Bell01,
    description: 'What has happened to you',
  },
];

/**
 * §7.2's CRM surface — the records, as opposed to the work being done for them.
 * It sits above Delivery so the two jobs stay distinct.
 *
 * The two entries have different audiences, and the capabilities say so.
 * Organizations is the sales side's surface and production teams never need it.
 * People is the directory, and everybody needs it — which is why it carries
 * `person.view`, held by all three employee roles and neither portal role
 * (migration 0014), rather than the `org.manage` beside it.
 */
const CRM_NAV: NavItem[] = [
  {
    to: '/people',
    label: 'People',
    capability: 'person.view',
    description: 'Everyone we have a record of, whether or not they can sign in',
  },
  {
    to: '/organizations',
    label: 'Organizations',
    capability: 'org.manage',
    description: 'Clients, prospects, vendors, partners — and their spaces',
  },
];

/**
 * The work itself: clients, then the projects under them.
 */
const DELIVERY_NAV: NavItem[] = [
  {
    to: '/clients',
    label: 'Clients',
    capability: 'org.manage',
    description: 'Organizations we are delivering for, and the work under each',
  },
  {
    to: '/projects',
    label: 'Projects',
    capability: 'project.view',
    description: 'The work, as rows rather than as folders',
  },
];

/**
 * Internal spaces — nobody's client and nobody's project — plus search, which
 * is how you find a page in them without walking the tree.
 */
const KNOWLEDGE_NAV: NavItem[] = [
  {
    to: '/knowledge',
    label: 'Knowledge',
    description:
      'Handbooks, playbooks, and references — nobody\u2019s client and nobody\u2019s project',
  },
  /**
   * Search carries no capability. §9.2 puts the ACL inside the one statement,
   * so every role can ask and the answer is already narrowed to what the asker
   * can reach — dimming this would say "your role does not carry" about a page
   * that works for everybody.
   */
  { to: '/search', label: 'Search', description: 'Documents, files, and links you can reach' },
];

/**
 * Below the divider: administration, which is a different job from the work
 * above it. Settings holds what changes how the workspace behaves — today that
 * is Users and nothing else. Schema is introspection, done by a different
 * person on a different surface, which is why the two stay separate.
 *
 * People used to be the whole of an "Admin" entry here. §7.3's argument for
 * moving it out of Schema — provisioning is a business task, not database
 * introspection — applies once more to the directory itself: reading it is not
 * provisioning either, so it now sits above the divider with the rest of the
 * day-to-day work.
 */
const ADMIN_NAV: NavItem[] = [
  {
    to: '/settings/workspace/users',
    label: 'Settings',
    capability: 'user.manage',
    description: 'Who can sign in, and as what — provisioning, invitations, offboarding',
  },
  {
    to: '/schema',
    label: 'Schema',
    capability: 'audit.view',
    description:
      'Effective access and the raw tree — the manual counterpart to the resolution suite',
  },
];

function NavLink({ item, role }: { item: NavItem; role: UserRole }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // `/settings/workspace/users` is the destination and `/settings` is the
  // section, so the highlight follows the section — otherwise somebody on a
  // settings page that is not Users sees nothing lit at all.
  const section = item.to.startsWith('/settings') ? '/settings' : item.to;
  const active = section === '/' ? pathname === '/' : pathname.startsWith(section);
  const carries = item.capability ? roleCarries(role, item.capability) : true;

  return (
    <Link
      to={item.to}
      className={cn(
        'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium no-underline transition-colors',
        // A white pill on the grey sidebar rather than a tinted one: with no
        // hue in the accent, the only contrast left between "this section" and
        // the chrome around it is the surface itself.
        active ? 'bg-primary text-primary shadow-xs' : 'text-secondary hover:bg-secondary-hover',
        !carries && 'opacity-55',
      )}
      title={
        carries
          ? item.description
          : `Your role does not carry ${item.capability}. ${item.description}`
      }
    >
      {item.icon ? <item.icon size={16} className="shrink-0" /> : null}
      {item.label}
      {item.to === '/notifications' ? <UnreadPill /> : null}
    </Link>
  );
}

function UnreadPill() {
  const { data } = useUnreadCount();
  if (!data || data.unreadCount === 0) return null;
  return <Badge tone="brand">{data.unreadCount}</Badge>;
}

function NavHeading({ children }: { children: ReactNode }) {
  return <p className="mt-2 px-2.5 pt-2 pb-1 text-xs font-semibold text-quaternary">{children}</p>;
}

export function AppShell() {
  const { data, isPending } = useMe();
  const signOut = useSignOut();
  const sidebar = useSidebar();

  if (isPending || !data) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Loading label="Resolving your session…" />
      </div>
    );
  }

  const user = data.user;

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      <aside id={SIDEBAR_ID} className={sidebarClasses(sidebar)} style={sidebarStyle(sidebar)}>
        {/* The inner panel keeps the sidebar's full width while the element
            around it animates to nothing, so the contents slide out of view
            rather than reflowing on the way. */}
        <div className="flex h-full w-full flex-col lg:w-[var(--sidebar-width)]">
          {/* `h-12` and a bottom border, which is the shell bar's height and
              the shell bar's border: the two sit side by side, so one line runs
              the width of the window rather than two that nearly meet. */}
          <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-secondary px-3">
            <Link to="/" className="flex min-w-0 items-center gap-2 no-underline">
              <span className="grid size-7 shrink-0 place-items-center rounded-md bg-brand-solid text-xs font-bold text-brand-on-solid">
                M
              </span>
              <span className="truncate text-sm font-semibold text-primary">Matchbox OS</span>
            </Link>
            <SidebarToggle sidebar={sidebar} />
          </div>

          <nav aria-label="Sections" className="flex flex-col gap-0.5 px-3 pt-4">
            {NAV.map((item) => (
              <NavLink key={item.to} item={item} role={user.role} />
            ))}

            <NavHeading>CRM</NavHeading>
            {CRM_NAV.map((item) => (
              <NavLink key={item.to} item={item} role={user.role} />
            ))}

            <NavHeading>Delivery</NavHeading>
            {DELIVERY_NAV.map((item) => (
              <NavLink key={item.to} item={item} role={user.role} />
            ))}

            <NavHeading>Knowledge</NavHeading>
            {KNOWLEDGE_NAV.map((item) => (
              <NavLink key={item.to} item={item} role={user.role} />
            ))}

            <span className="my-2 border-t border-secondary" aria-hidden="true" />

            {ADMIN_NAV.map((item) => (
              <NavLink key={item.to} item={item} role={user.role} />
            ))}
          </nav>

          <div className="mt-auto flex flex-col gap-3 border-t border-secondary px-3 py-4">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-primary">{user.name}</p>
              <p className="truncate text-xs text-tertiary">{user.email}</p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              isPending={signOut.isPending}
              onPress={() => {
                signOut.mutate(undefined, {
                  // A full navigation rather than a router push: the session
                  // cookie is gone, and reloading is the one way to be certain no
                  // cached query outlives it.
                  onSettled: () => window.location.assign('/sign-in'),
                });
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      <SidebarResizeHandle sidebar={sidebar} />

      <main className="flex min-w-0 flex-1 flex-col">
        <ShellBar sidebar={sidebar} />
        <div className="flex-1 px-4 py-6 sm:px-8 sm:py-8">
          <div className="mx-auto flex max-w-6xl flex-col gap-6">
            {/*
              Above the outlet rather than inside a page, because §7.7's audience
              is not reading the acknowledgments screen — that is the point of a
              nudge. It renders nothing when nothing is owed.
            */}
            <AcknowledgmentBanner />
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}

export function ShellSection({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-6">{children}</div>;
}
